import { Box, Card, CardContent, Chip, CircularProgress, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from '../../../shared/components';
import { cashFlowService } from '../../../shared/services';
import { IPropsBusinessPage } from '../Business';
import { dateFormat } from '../../../shared/helpers/dateFormat';

const formatCurrency = (value: number) => `R$ ${value.toFixed(2)}`;

export const BusinessCashFlow: React.FC<IPropsBusinessPage> = ({ business }) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const parsedStartDate = startDate ? startDate.toDate() : undefined;
  const parsedEndDate = endDate ? endDate.toDate() : undefined;

  const { data: cashFlow, isFetching: isFetchingCashFlow } = useQuery({
    queryKey: ['cashFlow', business.id, parsedStartDate, parsedEndDate],
    queryFn: () => cashFlowService.getAll(business.id, parsedStartDate, parsedEndDate),
  });

  const { data: cashFlowSummary, isFetching: isFetchingSummary } = useQuery({
    queryKey: ['cashFlowSummary', business.id, parsedStartDate, parsedEndDate],
    queryFn: () => cashFlowService.getSummary(business.id, parsedStartDate, parsedEndDate),
  });

  const isLoading = isFetchingCashFlow || isFetchingSummary;

  // Group by date
  const groupedData = useMemo(() => {
    if (!cashFlow) return {};
    return cashFlow.reduce((acc, entry) => {
      const dateKey = dateFormat(entry.date, 'YYYY-MM-DD');
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(entry);
      return acc;
    }, {} as Record<string, typeof cashFlow>);
  }, [cashFlow]);

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <Typography variant='h6'>Fluxo de Caixa Geral</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <DatePicker
            label="Data Inicial"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <DatePicker
            label="Data Final"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
          />
        </Grid>
      </Grid>

      {isLoading ? (
        <Box display='flex' justifyContent='center' py={6}><CircularProgress /></Box>
      ) : (
        <>
          {cashFlowSummary && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent>
                    <Typography variant='caption' color='text.secondary'>Agendamentos</Typography>
                    <Typography variant='h6' color='primary'>{formatCurrency(cashFlowSummary.totalAppointments)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent>
                    <Typography variant='caption' color='text.secondary'>Entrada Manual</Typography>
                    <Typography variant='h6' color='success.main'>{formatCurrency(cashFlowSummary.totalManualIn)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent>
                    <Typography variant='caption' color='text.secondary'>Saída Manual</Typography>
                    <Typography variant='h6' color='error.main'>{formatCurrency(cashFlowSummary.totalManualOut)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent>
                    <Typography variant='caption' color='text.secondary'>Saldo do Período</Typography>
                    <Typography variant='h6' color={cashFlowSummary.netCashFlow >= 0 ? 'success.main' : 'error.main'}>
                      {formatCurrency(cashFlowSummary.netCashFlow)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {Object.entries(groupedData).sort(([dateA], [dateB]) => dateB.localeCompare(dateA)).map(([date, entries]) => (
            <Box key={date} mt={2}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ borderBottom: '1px solid #ccc', pb: 1 }}>
                {dateFormat(new Date(`${date}T00:00:00`), 'DD/MM/YYYY')}
              </Typography>
              <Box display='flex' flexDirection='column' gap={1}>
                {entries.map((entry) => (
                  <Card key={`${entry.type}-${entry.id}`}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box display='flex' justifyContent='space-between' alignItems='center'>
                        <Box>
                          <Chip
                            label={entry.type}
                            color={entry.type === 'Agendamento' ? 'primary' : entry.amount >= 0 ? 'success' : 'error'}
                            size='small'
                            sx={{ mr: 1 }}
                          />
                          {entry.category && (
                            <Chip label={entry.category} size='small' variant='outlined' sx={{ mr: 1 }} />
                          )}
                          <Typography variant='body1' sx={{ mt: 1, display: 'inline-block' }}>{entry.description}</Typography>
                          <Typography variant='caption' color='text.secondary' display='block'>
                            {entry.createdByName?.trim() ? `${entry.createdByName} - ` : ''}{dateFormat(entry.date, 'HH:mm')}
                          </Typography>
                        </Box>
                        <Typography
                          variant='subtitle1'
                          fontWeight='bold'
                          color={entry.amount >= 0 ? 'success.main' : 'error.main'}
                        >
                          {entry.amount >= 0 ? '+' : ''}{formatCurrency(entry.amount)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          ))}

          {(!cashFlow || cashFlow.length === 0) && (
            <Typography color='text.secondary' textAlign='center' py={4}>
              Nenhuma movimentação encontrada
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};